// Geocoding service using Nominatim API (OpenStreetMap)
// Free to use, no API key required

interface GeocodingResult {
  lat: string;
  lon: string;
  display_name: string;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

// Cache to store geocoded addresses
const geocodingCache = new Map<string, Coordinates>();

/**
 * Geocode an address to latitude and longitude coordinates
 * Uses Nominatim API (OpenStreetMap) - free, no API key needed
 */
export const geocodeAddress = async (
  address: string,
  city: string,
  postalCode: string
): Promise<Coordinates | null> => {
  // Create a unique cache key
  const cacheKey = `${address}, ${postalCode} ${city}`;
  
  // Check cache first
  if (geocodingCache.has(cacheKey)) {
    return geocodingCache.get(cacheKey)!;
  }

  try {
    // Construct the query string
    const query = encodeURIComponent(cacheKey + ', France');
    const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=fr`;

    // Make the request with proper headers (Nominatim requires a User-Agent)
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'FullBook-Leisure-App/1.0',
      },
    });

    if (!response.ok) {
      console.error(`Geocoding failed for ${cacheKey}: HTTP ${response.status}`);
      return null;
    }

    const data: GeocodingResult[] = await response.json();

    if (data.length === 0) {
      console.warn(`No geocoding results found for: ${cacheKey}`);
      return null;
    }

    const result = data[0];
    const coordinates: Coordinates = {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
    };

    // Cache the result
    geocodingCache.set(cacheKey, coordinates);

    // Be nice to the API - add a small delay between requests
    await new Promise(resolve => setTimeout(resolve, 1000));

    return coordinates;
  } catch (error) {
    console.error(`Error geocoding address ${cacheKey}:`, error);
    return null;
  }
};

/**
 * Geocode multiple addresses in batch
 * Processes them sequentially to respect API rate limits
 */
export const geocodeAddressesBatch = async (
  addresses: Array<{
    address: string;
    city: string;
    postalCode: string;
  }>
): Promise<Array<Coordinates | null>> => {
  const results: Array<Coordinates | null> = [];

  for (const addr of addresses) {
    const coords = await geocodeAddress(addr.address, addr.city, addr.postalCode);
    results.push(coords);
  }

  return results;
};

/**
 * Pre-computed coordinates for known Île-de-France locations
 * This avoids API calls for activities we already know the coordinates for
 */
export const KNOWN_COORDINATES: Record<string, Coordinates> = {
  // These will be filled in as we geocode activities
  'Bowling Star Paris': { latitude: 48.8730, longitude: 2.3428 },
  'Koezio Escape Game': { latitude: 49.0000, longitude: 2.5167 },
  'RG Laser Game': { latitude: 48.8860, longitude: 2.2686 },
  'Urban Soccer Aubervilliers': { latitude: 48.9144, longitude: 2.3880 },
  'SpeedPark Karting': { latitude: 48.9945, longitude: 2.0829 },
  'Forest Hill Padel Club': { latitude: 48.6250, longitude: 2.4319 },
};

/**
 * Pre-computed coordinates for common Île-de-France cities
 * Used for proximity search to avoid repeated API calls
 */
export const KNOWN_CITY_COORDINATES: Record<string, Coordinates> = {
  'paris': { latitude: 48.8566, longitude: 2.3522 },
  'versailles': { latitude: 48.8049, longitude: 2.1342 },
  'rambouillet': { latitude: 48.6444, longitude: 1.8311 },
  'saint-denis': { latitude: 48.9362, longitude: 2.3574 },
  'neuilly-sur-seine': { latitude: 48.8860, longitude: 2.2686 },
  'boulogne-billancourt': { latitude: 48.8372, longitude: 2.2425 },
  'créteil': { latitude: 48.7906, longitude: 2.4547 },
  'nanterre': { latitude: 48.8917, longitude: 2.2067 },
  'aubervilliers': { latitude: 48.9144, longitude: 2.3880 },
  'bobigny': { latitude: 48.9076, longitude: 2.4391 },
  'montreuil': { latitude: 48.8639, longitude: 2.4422 },
  'évry': { latitude: 48.6250, longitude: 2.4319 },
  'roissy-en-france': { latitude: 49.0000, longitude: 2.5167 },
  'conflans-sainte-honorine': { latitude: 48.9945, longitude: 2.0829 },
  'levallois-perret': { latitude: 48.8931, longitude: 2.2875 },
  'pantin': { latitude: 48.8943, longitude: 2.3999 },
  'ivry-sur-seine': { latitude: 48.8139, longitude: 2.3864 },
  'bagneux': { latitude: 48.7969, longitude: 2.3186 },
};

/**
 * Get coordinates for an activity, using known coordinates if available
 */
export const getActivityCoordinates = async (
  name: string,
  address: string,
  city: string,
  postalCode: string
): Promise<Coordinates | null> => {
  // Check if we have pre-computed coordinates
  if (KNOWN_COORDINATES[name]) {
    return KNOWN_COORDINATES[name];
  }

  // Otherwise, geocode the address
  return geocodeAddress(address, city, postalCode);
};

/**
 * Geocode a city name to coordinates
 * Checks known cities first, then falls back to API
 * @param cityName Name of the city to geocode
 * @returns Coordinates or null if not found
 */
export const geocodeCity = async (cityName: string): Promise<Coordinates | null> => {
  if (!cityName) return null;
  
  // Normalize city name for lookup (lowercase, trim)
  const normalizedCity = cityName.toLowerCase().trim();
  
  // Check if we have pre-computed coordinates for this city
  if (KNOWN_CITY_COORDINATES[normalizedCity]) {
    return KNOWN_CITY_COORDINATES[normalizedCity];
  }
  
  // Check cache
  const cacheKey = `city:${normalizedCity}`;
  if (geocodingCache.has(cacheKey)) {
    return geocodingCache.get(cacheKey)!;
  }
  
  // Fall back to geocoding API
  try {
    const query = encodeURIComponent(`${cityName}, Île-de-France, France`);
    const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=fr`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'FullBook-Leisure-App/1.0',
      },
    });
    
    if (!response.ok) {
      console.error(`City geocoding failed for ${cityName}: HTTP ${response.status}`);
      return null;
    }
    
    const data: GeocodingResult[] = await response.json();
    
    if (data.length === 0) {
      console.warn(`No geocoding results found for city: ${cityName}`);
      return null;
    }
    
    const result = data[0];
    const coordinates: Coordinates = {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
    };
    
    // Cache the result
    geocodingCache.set(cacheKey, coordinates);
    
    // Be nice to the API - add a small delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return coordinates;
  } catch (error) {
    console.error(`Error geocoding city ${cityName}:`, error);
    return null;
  }
};

